/**
 * @file Renders the table of personal access tokens and its related modals.
 */
import React, { useRef, useState, useCallback } from "react";
import { Alert, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Table, { ASCEND } from "@components/Table";
import { W1 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { indexMyPersonalAccessTokensOperation as indexOperation } from "@api/services/identity";
import {
  type CreatedPersonalAccessToken,
  type PersonalAccessToken,
} from "@api/types";

import useColumnsConfig from "./helpers/useColumnsConfig";
import PersonalAccessTokenCreateModal, {
  type PersonalAccessTokenCreateModalHandle,
} from "./PersonalAccessTokenCreateModal";
import PersonalAccessTokenSecretModal from "./PersonalAccessTokenSecretModal";
import PersonalAccessTokenDetailsModal from "./PersonalAccessTokenDetailsModal";

const LABEL_TITLE = "Personal Access Tokens";
const LABEL_CREATE = "Create Token";
const DESCRIPTION = "Tokens let non-interactive clients act on your behalf. Each token carries your own access, so treat it like a password.";
const LABEL_SECOND_FACTOR_OFF = "Two factor authentication is off";
const DESCRIPTION_SECOND_FACTOR_OFF = "Enable two factor authentication in the profile above to create a token. A token carries your own access, therefore the account must have the second factor.";

type TableHandle = {
  addItem: (item: PersonalAccessToken) => void;
  updateItem: (id: string, attributes: Partial<PersonalAccessToken>) => void;
};

/**
 * Renders the token table, its create action and the create, secret and
 * details modals it opens.
 */
const PersonalAccessTokensTable = (): JSX.Element => {
  // NOTE: personal access tokens belong to the identity rather than to the
  //       organization membership, so they are indexed with the
  //       authentication token instead of the authorization one.
  const { identity, authenticatedRequest } = useAppContext() as AppContext;

  // NOTE: a token is a long lived credential that carries the access of its
  //       owner, therefore the second factor must protect the account first.
  const shouldBlockCreate = identity?.is2FaEnabled !== true;

  const table = useRef<TableHandle>({} as TableHandle);
  const createModal = useRef<
    PersonalAccessTokenCreateModalHandle | Record<string, never>
  >({});

  const [ createdToken, setCreatedToken ] =
    useState<CreatedPersonalAccessToken>();

  const [ selectedToken, setSelectedToken ] = useState<PersonalAccessToken>();

  /**
   * Adds the newly created token to the table and opens the secret modal.
   */
  const onCreated = (newToken: CreatedPersonalAccessToken) => {
    table.current.addItem(newToken);
    setCreatedToken(newToken);
  };

  // NOTE: the columns config keeps its identity while these handlers keep
  //       theirs, and a new config makes the table load the items again.
  const onRevoked = useCallback((revokedToken: PersonalAccessToken) =>
    table.current.updateItem(revokedToken.id, revokedToken)
  , []);

  const onSelected = useCallback((newSelectedToken: PersonalAccessToken) =>
    setSelectedToken(newSelectedToken)
  , []);

  // NOTE: the details of the new token open after the secret, because the
  //       client needs each step of the exchange and the specification, and
  //       not the secret alone.
  /**
   * Closes the secret modal and opens the details modal for the same token.
   */
  const closeSecretModal = () => {
    setSelectedToken(createdToken);
    setCreatedToken(undefined);
  };

  /**
   * Closes the details modal.
   */
  const closeDetailsModal = () => setSelectedToken(undefined);

  /**
   * Opens the create-token modal, unless the second factor blocks creation.
   */
  const openCreateModal = () => {
    if (shouldBlockCreate) {
      return;
    }

    createModal.current.open?.();
  };

  const columnsConfig = useColumnsConfig({ onSelected, onRevoked });

  const headerActions = [
    {
      icon: <PlusOutlined />,
      action: openCreateModal,
      title: LABEL_CREATE,
      disabled: shouldBlockCreate,
    },
  ];

  const alertStyle = {
    marginBottom: W1,
  };

  const renderedDescription = shouldBlockCreate
    ? (
      <Alert
        showIcon
        type="warning"
        style={alertStyle}
        message={LABEL_SECOND_FACTOR_OFF}
        description={DESCRIPTION_SECOND_FACTOR_OFF}
      />
    )
    : (
      <Typography.Paragraph type="secondary">
        {DESCRIPTION}
      </Typography.Paragraph>
    );

  const tableProps = {
    size: "small" as const,
    table,
    request: authenticatedRequest,
    className: "last-column-action",
    headerActions,
    columnsConfig,
    indexOperation,
    defaultSortColumn: "name",
    defaultSortColumnDirection: ASCEND,
  };

  return (
    <>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {LABEL_TITLE}
      </Typography.Title>

      {renderedDescription}

      <Table {...tableProps} />

      <PersonalAccessTokenCreateModal
        modal={createModal}
        onSuccess={onCreated}
      />

      <PersonalAccessTokenSecretModal
        isOpen={!!createdToken}
        name={createdToken?.name}
        secretToken={createdToken?.secretToken}
        onClose={closeSecretModal}
      />

      <PersonalAccessTokenDetailsModal
        isOpen={!!selectedToken}
        personalAccessToken={selectedToken}
        onClose={closeDetailsModal}
      />
    </>
  );
};

export default PersonalAccessTokensTable;
